import { Core } from "@faire/design-tokens";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web/common/media";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { IconButton } from "@faire/web/slate/IconButton";
import { Close } from "@faire/web/slate/icons/foundation/Close";
import { Row, Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useRefFrom } from "@faire/web/ui/hooks/useRefFrom";
import * as React from "react";
import ReactDOM from "react-dom";
import styled, { css, keyframes } from "styled-components";

const ANIMATION_DURATION = 200;
const ANIMATION_TRANSLATE = 100;

enum BottomSheetStage {
  OPENED = "OPENED",
  CLOSING = "CLOSING",
  CLOSED = "CLOSED",
}
interface IProps {
  children?: React.ReactNode | undefined;
  onClose?: () => void;
  title?: React.ReactNode;
  isOpen: boolean;
  inModal?: boolean;
  condensed?: boolean;
}

export const BottomSheet: React.FC<IProps> = ({
  children,
  onClose,
  title,
  isOpen,
  inModal,
  condensed,
}) => {
  const { current: portalElement } = useRefFrom(() => {
    const element = getDocumentOrThrow().createElement("div");
    element.setAttribute("data-test-id", "bottom-sheet");
    return element;
  });

  const isSSR = useIsSSR();

  const [stage, setStage] = React.useState<BottomSheetStage>(
    isOpen ? BottomSheetStage.OPENED : BottomSheetStage.CLOSED
  );

  React.useEffect(() => {
    if (isOpen) {
      setStage(BottomSheetStage.OPENED);
    } else {
      setStage((stage) =>
        stage !== BottomSheetStage.CLOSED ? BottomSheetStage.CLOSING : stage
      );
    }
  }, [isOpen]);

  React.useEffect(() => {
    getDocumentOrThrow().body.appendChild(portalElement);
    return () => {
      getDocumentOrThrow().body.removeChild(portalElement);
    };
  }, [portalElement]);

  if (isSSR || stage === BottomSheetStage.CLOSED) {
    return null;
  }

  return ReactDOM.createPortal(
    <Overlay
      $stage={stage}
      $inModal={inModal}
      onClick={() => {
        onClose?.();
      }}
      data-test-id="bottom-sheet-overlay"
      onAnimationEnd={() =>
        setStage((stage) =>
          stage === BottomSheetStage.CLOSING ? BottomSheetStage.CLOSED : stage
        )
      }
    >
      <Container
        $stage={stage}
        $condensed={condensed}
        onClick={(event: { stopPropagation: () => void }) => {
          event.stopPropagation();
        }}
      >
        {!condensed ? (
          <HeaderContainer justify="right">
            <IconButton
              variant="tertiary"
              onClick={() => {
                onClose?.();
              }}
              aria-label="Close"
            >
              <Close data-test-id="closeButton" />
            </IconButton>
          </HeaderContainer>
        ) : (
          <AbsoluteIconButton
            variant="tertiary"
            onClick={() => {
              onClose?.();
            }}
            aria-label="Close"
          >
            <Close data-test-id="closeButton" />
          </AbsoluteIconButton>
        )}
        {title ? (
          <>
            <BodyContainer>
              <Typography variant="pageHeaderSerifRegular">{title}</Typography>
            </BodyContainer>
            <Spacer width="4x" />
          </>
        ) : null}
        <BodyContainer>{children}</BodyContainer>
      </Container>
    </Overlay>,
    portalElement
  );
};

const fadeIn = keyframes`
  0 {opacity: 0;}
  100% {opacity: 1;}
`;

const fadeOut = keyframes`
  0 {opacity: 1;}
  100% {opacity: 0;}
`;

const slideIn = keyframes`
  0 {transform: translateY(${ANIMATION_TRANSLATE}px);}
  100% {transform: translateY(0);}
`;

const slideOut = keyframes`
  0 {transform: translateY(0);}
  100% {transform: translateY(${ANIMATION_TRANSLATE}px);}
`;

const Overlay = styled(Column).attrs({
  align: "center",
  justify: "flex-end",
})<{
  $stage: BottomSheetStage;
  $inModal?: boolean;
}>`
  max-height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${(props) => (props.$inModal ? "1001" : "12")};
  ${({ $stage: stage }) => {
    if (stage === BottomSheetStage.OPENED) {
      return css`
        opacity: 0;
        animation: ${fadeIn} ${ANIMATION_DURATION}ms ease-out 0s 1 forwards;
      `;
    } else if (stage === BottomSheetStage.CLOSING) {
      return css`
        animation: ${fadeOut} ${ANIMATION_DURATION}ms ease-in 0s 1 forwards;
      `;
    }
    return "";
  }}
`;

// This ContentContainer is necessary to deal with smaller screens, when it's necessary to scroll
const BodyContainer = styled(Column)`
  max-height: 100%;
  overflow-y: auto;
  padding-left: ${getSpacing("4x")};
  padding-right: ${getSpacing("4x")};
`;

const Container = styled(Column)<{ $stage: BottomSheetStage; $condensed?: boolean }>`
  width: 100%;
  max-width: ${MIN_WIDTH_BREAKPOINTS.TABLET - 1}px;
  padding-bottom: ${getSpacing("3x")};
  max-height: 85%;
  background-color: ${Core.surface.inverse};
  border-radius: 10px 10px 0px 0px;
  ${({ $stage: stage, $condensed: condensed }) => {
    if (stage === BottomSheetStage.OPENED) {
      return css`
        transform: translateY(${ANIMATION_TRANSLATE}px);
        animation: ${slideIn} ${ANIMATION_DURATION}ms ease-out 0s 1 forwards;
        padding-top: ${condensed ? getSpacing("3x") : undefined};
      `;
    } else if (stage === BottomSheetStage.CLOSING) {
      return css`
        animation: ${slideOut} ${ANIMATION_DURATION}ms ease-in 0s 1 forwards;
        padding-top: ${condensed ? getSpacing("3x") : undefined};
      `;
    }
    return "";
  }}
`;

const HeaderContainer = styled(Row).attrs({
  align: "center",
})`
  padding: ${getSpacing("2x")};
`;

const AbsoluteIconButton = styled(IconButton)`
  position: absolute;
  top: ${getSpacing("1x")};
  right: ${getSpacing("1x")};
`;
